import CorporateFareRoundedIcon from '@mui/icons-material/CorporateFareRounded'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import type { Customer, CustomerGroupTreeNode } from '@cmms/api-client'
import type { Site } from '@cmms/types'

/// Merges three independent endpoints — customer-groups tree,
/// customers list, sites list — into one client-side display tree:
/// Group -> Sub Group -> Customer -> Site. Extends the same merge
/// `CustomerSwitcher.tsx` already does for Group -> Sub Group ->
/// Customer by folding in Sites as Customer children. Locations are
/// deliberately not part of this tree — they stay on their own
/// dedicated page.
export type OrgNodeKind = 'group' | 'subgroup' | 'customer' | 'site'

export type OrgNode =
  | { kind: 'group' | 'subgroup'; id: string; name: string; children: OrgNode[] }
  | { kind: 'customer'; id: string; name: string; siteCount?: number; children: OrgNode[] }
  | { kind: 'site'; id: string; name: string; children: [] }

/// One distinct icon + color per kind, shared by the tree rows and the
/// detail-pane header badge so the same node always reads the same way
/// wherever it's shown. Deliberately four different glyph *shapes* (not
/// just four colors) — Customer used to share Site's plain building
/// icon (color was the only differentiator), and Group's "corporate
/// building" read as near-identical to Customer's at 17px next to it.
export const ORG_NODE_STYLE: Record<OrgNodeKind, { icon: typeof CorporateFareRoundedIcon; color: string }> = {
  group:    { icon: CorporateFareRoundedIcon, color: '#4F46E5' }, // indigo
  subgroup: { icon: AccountTreeOutlinedIcon,  color: '#7C3AED' }, // violet
  customer: { icon: WorkOutlineIcon,          color: '#D97706' }, // amber
  site:     { icon: BusinessOutlinedIcon,     color: '#0D9488' }, // teal
}

export function buildOrgTree(
  groups: CustomerGroupTreeNode[],
  customers: Customer[],
  sites: Site[],
): OrgNode[] {
  const sitesByCustomer = new Map<string, Site[]>()
  for (const s of sites) {
    const list = sitesByCustomer.get(s.customer_id) ?? []
    list.push(s)
    sitesByCustomer.set(s.customer_id, list)
  }

  function customerNode(c: Customer): OrgNode {
    const siteChildren: OrgNode[] = (sitesByCustomer.get(c.id) ?? []).map((s) => ({
      kind: 'site' as const, id: s.id, name: s.name, children: [],
    }))
    return { kind: 'customer', id: c.id, name: c.name, siteCount: c.site_count, children: siteChildren }
  }

  const byGroupId = new Map<string, Customer[]>()
  for (const c of customers) {
    if (!c.group_id) continue
    const list = byGroupId.get(c.group_id) ?? []
    list.push(c)
    byGroupId.set(c.group_id, list)
  }

  function convert(node: CustomerGroupTreeNode): OrgNode {
    const isSubGroup = Boolean(node.parent_id)
    const customerLeaves: OrgNode[] = isSubGroup
      ? (byGroupId.get(node.id) ?? []).map(customerNode)
      : []
    return {
      kind: isSubGroup ? 'subgroup' : 'group',
      id: node.id,
      name: node.name,
      children: [...node.children.map(convert), ...customerLeaves],
    }
  }

  const groupNodes = groups.map(convert)
  const ungrouped: OrgNode[] = customers.filter((c) => !c.group_id).map(customerNode)
  return [...groupNodes, ...ungrouped]
}

export function findOrgNode(nodes: OrgNode[], id: string): OrgNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    const found = findOrgNode(n.children, id)
    if (found) return found
  }
  return null
}
